import { useMemo, useState } from 'react';
import { SalesTermsMapper } from 'shared/mappers';

import { AgAutocomplete, AgDatePicker, AgInput, AgText, fromCalendarDate, toCalendarDate } from 'src/components/ui';

import useCustomerPortalApi from '../useCustomerPortalApi';
import useCustomerPortalLocations from '../useCustomerPortalLocations';

import { CustomerPortalPurchaseOrderData } from './useCustomerPortalPurchaseOrder';

export default function OrderDetails({ purchaseOrder }: { purchaseOrder: CustomerPortalPurchaseOrderData }) {
  const { updatePurchaseOrder } = useCustomerPortalApi();
  const [orderDate, setOrderDate] = useState(purchaseOrder.orderDate);
  const [shipToId, setShipToId] = useState(purchaseOrder.shipment?.destinationId);
  const [billToId, setBillToId] = useState(purchaseOrder.billToId);

  const locations = useCustomerPortalLocations();

  const shipTo = useMemo(() => locations?.shipping?.find((l) => l.id === shipToId), [locations, shipToId]);
  const billTo = useMemo(() => locations?.billing?.find((l) => l.id === billToId), [locations, billToId]);

  if (!locations) return null;

  const isDraft = purchaseOrder.status === 'DRAFT';

  const Field = ({ label, displayValue, children }) => (
    <div className="w-full sm:w-[calc(50%-8px)] md:w-[calc(33.333%-11px)]">
      {!isDraft ? (
        <div className="flex flex-col">
          <AgText size="12" className="uppercase tracking-wider text-neutral-8">
            {label}
          </AgText>
          <AgText size="16">{displayValue}</AgText>
        </div>
      ) : (
        children
      )}
    </div>
  );

  return (
    <div className="flex flex-wrap gap-4">
      <Field label="Customer" displayValue={purchaseOrder.businessEntity.name}>
        <AgInput label="Customer Name" isDisabled value={purchaseOrder.businessEntity.name} />
      </Field>
      <Field label="Order Date" displayValue={new Date(purchaseOrder.orderDate)?.toLocaleDateString()}>
        <AgDatePicker
          label="Order Date"
          value={toCalendarDate(orderDate)}
          onChange={(value) => {
            const date = fromCalendarDate(value);
            setOrderDate(date);
            updatePurchaseOrder(purchaseOrder.id, { orderDate: date });
          }}
        />
      </Field>
      <Field label="Ship To" displayValue={shipTo?.label ?? '--'}>
        <AgAutocomplete
          value={shipTo}
          label="Ship To"
          options={locations.shipping}
          getOptionLabel={(l) => l.label}
          isDisabled={locations.shipping.length < 2}
          onChange={(value) => {
            setShipToId(value?.id);
            updatePurchaseOrder(purchaseOrder.id, { shipToId: value?.id });
          }}
        />
      </Field>
      <Field label="Bill To" displayValue={billTo?.label ?? '--'}>
        <AgAutocomplete
          value={billTo}
          label="Bill To"
          options={locations.billing}
          getOptionLabel={(l) => l.label}
          isDisabled={locations.billing.length < 2}
          onChange={(value) => {
            setBillToId(value?.id);
            updatePurchaseOrder(purchaseOrder.id, { billToId: value?.id });
          }}
        />
      </Field>
      <Field
        label="Sales Terms"
        displayValue={purchaseOrder.salesTerms ? SalesTermsMapper.getLabel(purchaseOrder.salesTerms) : '--'}
      >
        <AgInput label="Sales Terms" isDisabled value={purchaseOrder.salesTerms} />
      </Field>
    </div>
  );
}
